import { getDataGridToolbarProps } from 'features/shared/utility/dataGridToolbarHelper';
import usePermissions from 'features/shared/hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'features/shared/constants/permissionResources';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { CommonDataGrid } from 'features/shared/components/table';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { RMSButton } from '../../../shared/components/buttons';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import { notify } from 'reducers/NotifierReducer';
import PlaceContactDialog from './PlaceContactDialog';
import { deletePersonContact } from '../../services/userService';
import { getPlaceContacts } from '../../services/placeService';
import { CONTACT_GRID_COLUMNS } from '../../constants/placeContactsTabConstants';
import FormSpinner from 'components/FormSpinner';

const PlaceContactsTab = ({ ptsPlaceID }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);

  const placePerms = usePermissions(PERMISSION_RESOURCES.PLACE);
  const canEditPlace = placePerms.Edit;

  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  const loadContacts = useCallback(async () => {
    if (!ptsPlaceID) return;

    await executeApiAction({
      setLoading,
      dispatch,
      action: () => getPlaceContacts(ptsPlaceID),
      onSuccess: (data) => setContacts(data),
    });
  }, [dispatch, ptsPlaceID]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  const handleAdd = useCallback(() => {
    setEditingContact(null);
    setFormOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setEditingContact(row);
    setFormOpen(true);
  }, []);

  const handleFormClose = useCallback(() => {
    setFormOpen(false);
    setEditingContact(null);
  }, []);

  const handleSaved = useCallback(() => {
    handleFormClose();
    loadContacts();
  }, [handleFormClose, loadContacts]);

  const performDelete = useCallback(
    async (row) => {
      await executeApiAction({
        setLoading,
        dispatch,
        action: () => deletePersonContact(row.ptsInfoID),
        onSuccess: () => {
          dispatch(notify('Contact deleted successfully', 'success'));
          loadContacts();
        },
      });
    },
    [dispatch, loadContacts]
  );

  const handleDelete = useCallback(
    (row) => {
      requestConfirm(
        `Are you sure you want to permanently delete this "${
          row.ContactMethod || 'contact'
        }" record?`,
        () => performDelete(row),
        'Delete Contact Method'
      );
    },
    [performDelete, requestConfirm]
  );

  return (
    <Box className="person-names-tab__grid-wrap place-grid-tab">
      {loading && <FormSpinner />}
      <Box className="person-addresses-tab__toolbar">
        <RMSButton
          label="Add Contact Method"
          prefixIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={!canEditPlace}
        />
      </Box>

      <CommonDataGrid
        rows={contacts}
        columns={CONTACT_GRID_COLUMNS}
        getRowId={(row) => row.ptsInfoID}
        toolbar={getDataGridToolbarProps({ printTitle: 'Place Contact Methods' })}
        actions={{
          edit: { visible: true, onClick: handleEdit, disabled: !canEditPlace },
          delete: { visible: true, onClick: handleDelete, disabled: !canEditPlace },
        }}
      />

      {formOpen && (
        <PlaceContactDialog
          open={formOpen}
          ptsPlaceID={ptsPlaceID}
          contact={editingContact}
          onClose={handleFormClose}
          onSaved={handleSaved}
        />
      )}

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </Box>
  );
};

export default PlaceContactsTab;
